import useGetAlterationPrerequisites from "@entities/campaigns/lib/hooks/useGetAlterationPrerequisites";
import {
  ICampaignAlterationApprovalRequest,
  ICampaignAlterationRequestPayload,
} from "@entities/campaigns/lib/types";
import { BriefCampaignInf } from "@shared/lib/types/briefCampains";
import { IPurchaseItems } from "@shared/lib/types/deliverableConfigurator";
import { formatData } from "@shared/lib/utils/formatData";
import { DeliverablePillSummary } from "@shared/ui";
import { Card, Flex, Spin, Tag, Typography } from "antd";
import React, { useMemo } from "react";

const formatCommercial = (cost?: number | null) => {
  if (cost === undefined || cost === null) return "—";
  return `₹${formatData(cost, "0.00")}`;
};

const typeColors: Record<string, string> = {
  "Reimbursement": "cyan",
  "Cost-Adjustment": "purple",
  "Brand-Cancellation": "magenta",
  "Influencer-Cancellation": "volcano",
  "Deliverable-Adjustment": "geekblue",
};

const statusColors: Record<string, string> = {
  "Approved": "blue",
  "Rejected": "red",
  "Pending": "orange",
};

interface AlterationHistoryItemCardProps {
  alterationItem: ICampaignAlterationApprovalRequest;
  briefId?: string;
  campaignInfluencer?: BriefCampaignInf;
}

export const AlterationHistoryItemCard: React.FC<AlterationHistoryItemCardProps> = ({
  alterationItem,
  briefId,
  campaignInfluencer,
}) => {
  const isPending = alterationItem.status === "Pending";

  const prerequisitePayload: ICampaignAlterationRequestPayload = useMemo(
    () => ({
      campaignInfluencerId: alterationItem.campaignInfluencerId,
      type: alterationItem.type,
      remarks: alterationItem.remarks || "",
      proofUploadUrl: alterationItem.proofUploadUrl,
      toAlterBrandCost: alterationItem.toAlterBrandCost ?? false,
      brandCostBaseCost: alterationItem.brandCostBaseCost || 0,
      brandCostDirection: alterationItem.brandCostDirection || "add",
      applyBrandCostServiceCostRate: alterationItem.applyBrandCostServiceCostRate,
      applyBrandCostGstRate: alterationItem.applyBrandCostGstRate,
      toAlterInfluencerCost: alterationItem.toAlterInfluencerCost ?? false,
      influencerCostBaseCost: alterationItem.influencerCostBaseCost || 0,
      influencerCostDirection: alterationItem.influencerCostDirection || "add",
      applyInfluencerCostGstRate: alterationItem.applyInfluencerCostGstRate,
      applyInfluencerCostTdsRate: alterationItem.applyInfluencerCostTdsRate,
    }),
    [alterationItem]
  );

  const { alterationPrerequisite, isAlterationPrerequisiteFetching } =
    useGetAlterationPrerequisites(prerequisitePayload, isPending && !alterationItem.preCheckResult && !["Influencer-Cancellation","Brand-Cancellation"].includes(alterationItem.type));

  // Costs resolution:
  // Prefer alterationItem.preCheckResult if present; otherwise fallback to alterationPrerequisite (pre-check call)
  const oldInfluencerCost =
    alterationItem.preCheckResult?.old?.totalInfluencerBaseCost ??
    alterationPrerequisite?.data?.old?.totalInfluencerBaseCost;

  const newInfluencerCost =
    alterationItem.preCheckResult?.new?.totalInfluencerBaseCost ??
    alterationPrerequisite?.data?.new?.totalInfluencerBaseCost;

  const oldBrandCost =
    alterationItem.preCheckResult?.old?.totalBrandSellingCost ??
    alterationPrerequisite?.data?.old?.totalBrandSellingCost;

  const newBrandCost =
    alterationItem.preCheckResult?.new?.totalBrandSellingCost ??
    alterationPrerequisite?.data?.new?.totalBrandSellingCost;

  // Deliverables calculation for Deliverable-Adjustment or deliverable changes
  const hasDeliverableChanges =
    alterationItem.type === "Deliverable-Adjustment";

  let oldPurchaseItems: IPurchaseItems[] = [];
  let newPurchaseItems: IPurchaseItems[] = [];

  if (hasDeliverableChanges) {
    const isCompletedStatus =
      alterationItem.status === "Approved" || alterationItem.status === "Rejected";

    const source = isCompletedStatus
      ? alterationItem.preChangedDeliverablePurchaseItems
      : campaignInfluencer?.purchaseItems;

    oldPurchaseItems = (source ?? []);

    newPurchaseItems = 
    // isCompletedStatus
    //   ? (campaignInfluencer?.purchaseItems ?? [])
      // : 
      [
          ...oldPurchaseItems.map((item) =>
            alterationItem.cancelledDeliverablePurchaseItemIds?.includes(item._id ?? "")
              ? { ...item, isCancelled: true }
              : item
          ),
          ...(alterationItem.addDeliverablePurchaseItems ?? []),
        ];
  }

  const typeTagColor = typeColors[alterationItem.type] || "default";
  const statusTagColor = statusColors[alterationItem.status] || "orange";

  const formattedDate = alterationItem.createdAt
    ? new Date(alterationItem.createdAt).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

  const isReimbursement = alterationItem.type === "Reimbursement";
  const reimbursementAmount =
    alterationItem.brandCostBaseCost || alterationItem.influencerCostBaseCost || 0;

  const showCosts =
    !isReimbursement &&
    (oldInfluencerCost != null ||
      newInfluencerCost != null ||
      oldBrandCost != null ||
      newBrandCost != null);

  return (
    <Card
      size="small"
      style={{
        backgroundColor: "#fcfdfe",
        borderColor: "#e3e8ee",
        borderRadius: 8,
        width: "100%",
      }}
      styles={{
        body: { padding: "12px 14px" },
      }}
    >
      <Flex vertical gap={8}>
        <Flex justify="space-between" align="center" wrap="wrap" gap={6}>
          <Flex align="center" gap={6}>
            <Tag color={typeTagColor} style={{ margin: 0, fontSize: 11, fontWeight: 700, borderRadius: 12 }}>
              {alterationItem.type}
            </Tag>
            <Tag color={statusTagColor} style={{ margin: 0, fontSize: 11, fontWeight: 600, borderRadius: 12 }}>
              {alterationItem.status || "Pending"}
            </Tag>
          </Flex>
          {formattedDate && (
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              {formattedDate}
            </Typography.Text>
          )}
        </Flex>

        {alterationItem.remarks && (
          <Typography.Text style={{ fontSize: 12, color: "#4f566b" }}>
            Remarks: {alterationItem.remarks}
          </Typography.Text>
        )}

        {isReimbursement ? (
          <Flex vertical gap={4} style={{ backgroundColor: "#f8fafc", padding: "8px 10px", borderRadius: 6 }}>
            <Flex justify="space-between" align="center" style={{ fontSize: 12 }}>
              <Typography.Text type="secondary" style={{ fontSize: 11, fontWeight: 600 }}>
                REIMBURSEMENT AMOUNT:
              </Typography.Text>
              <Typography.Text style={{ fontSize: 12, fontWeight: 600 }}>
                {formatCommercial(reimbursementAmount)}
              </Typography.Text>
            </Flex>
          </Flex>
        ) : isPending && isAlterationPrerequisiteFetching && !alterationItem.preCheckResult ? (
          <Flex align="center" gap={8} style={{ padding: "6px 0" }}>
            <Spin size="small" />
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              Calculating pre-check costs...
            </Typography.Text>
          </Flex>
        ) : (
          showCosts && (
            <Flex vertical gap={4} style={{ backgroundColor: "#f8fafc", padding: "8px 10px", borderRadius: 6 }}>
              {(oldInfluencerCost != null || newInfluencerCost != null) && (
                <Flex justify="space-between" align="center" style={{ fontSize: 12 }}>
                  <Typography.Text type="secondary" style={{ fontSize: 11, fontWeight: 600 }}>
                    INFLUENCER COST:
                  </Typography.Text>
                  <Typography.Text style={{ fontSize: 12, fontWeight: 600 }}>
                    {formatCommercial(oldInfluencerCost)} → {formatCommercial(newInfluencerCost)}
                  </Typography.Text>
                </Flex>
              )}

              {(oldBrandCost != null || newBrandCost != null) && (
                <Flex justify="space-between" align="center" style={{ fontSize: 12 }}>
                  <Typography.Text type="secondary" style={{ fontSize: 11, fontWeight: 600 }}>
                    BRAND COST:
                  </Typography.Text>
                  <Typography.Text style={{ fontSize: 12, fontWeight: 600 }}>
                    {formatCommercial(oldBrandCost)} → {formatCommercial(newBrandCost)}
                  </Typography.Text>
                </Flex>
              )}
            </Flex>
          )
        )}

        {/* Deliverable Alteration Addon */}
        {hasDeliverableChanges && (
          <Flex vertical gap={4} style={{ marginTop: 4 }}>
            <Typography.Text type="secondary" style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.04em" }}>
              DELIVERABLE CHANGES:
            </Typography.Text>
           <Flex align="center">
              <DeliverablePillSummary
                deliverable={{
                  briefId: briefId || "",
                  purchaseItems: oldPurchaseItems,
                }}
              />
              <span style={{ color: "#888", fontWeight: 600 }}>→</span>
              <DeliverablePillSummary
                deliverable={{
                  briefId: briefId || "",
                  purchaseItems: newPurchaseItems,
                }}
              />
            </Flex>
          </Flex>
        )}
      </Flex>
    </Card>
  );
};
